import '../../billing-management/style.scss';
import getFormatMessage from '../../../../../locales/get-format-message';
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import downloadBalanceIcon from '../../../../../assets/svg/download-balance.svg';
import {
  IBillList,
  IBillOrder,
  BillOrderArea,
  BillOrderMode,
  BillOrderType,
} from '../../../../../model/billing-center/balance';
import { downCSVTemplate } from '../../../../studio/organization-router/detail-table/member/lib/csv';
import { getLocalTime, getPrice } from '../../../../../lib';
import { AutoTable } from '../../../../../components/auto-table';
import { IListItem } from '../../../../../components/auto-table/model';
import { getBillList, fetchGameList } from '../../../../../api';
import { fetchEnvCollectionList } from '../../../../../api/game/env';
import { IAPIGameItem } from '../../../../../model/api-modules';
import { formatEnvCollectionOptions } from '../../../../game-content/game-filter/env';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../../../store/reducers';

interface IProps {
  readonly studioId: string;
  readonly dateRange: string;
  readonly isDateRangeSelected: boolean;
}

export const BillingManagementDetails = (props: IProps) => {
  const { studioId, dateRange, isDateRangeSelected } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const [billList, setBillList] = useState<IBillList[]>([]);
  const [originalData, setOriginalData] = useState<IBillList[]>([]);
  const [gameList, setGameList] = useState<ReadonlyArray<IAPIGameItem>>([]);
  const [allEnvList, setAllEnvList] = useState<any>({});
  // 筛选处理
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isDisabledDown, setIsDisabledDown] = useState<boolean>(false);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const isMounted = useRef(false);
  const getWithDrawaList = async () => {
    setLoading(true);
    const res = await getBillList(studioId, 1, 99999);
    const items = res?.list;
    if (items) {
      setBillList(items);
      setOriginalData(items);
      handleFilter(items);
    }
    setLoading(false);
  };

  const handleFilter = async (currentList?: IBillList[]) => {
    // 调接口时拿不到最新的值,可以直接传过来
    const currentData = currentList || originalData;
    const filtered = currentData.filter((item) => {
      const startDate = moment(item.bill_start_date, 'YYYY-MM-DD');
      const endDate = moment(item.bill_end_date, 'YYYY-MM-DD');
      const filterTime = moment(dateRange, 'YYYY-MM');
      const dateRangeMatch =
        !isDateRangeSelected ||
        filterTime.isBetween(startDate, endDate, null, '[]');
      return dateRangeMatch;
    });
    setBillList(filtered);
  };
  useEffect(() => {
    getGameAndEnv();
  }, []);

  useEffect(() => {
    getWithDrawaList();
  }, []);

  useEffect(() => {
    const isDisabledDown = !(billList.length > 0);
    setIsDisabledDown(isDisabledDown);
  }, [billList]);

  useEffect(() => {
    // 初始化拿不到数据，不执行
    if (isMounted.current) {
      handleFilter();
      setLoading(false);
    } else {
      isMounted.current = true;
    }
  }, [dateRange]);

  const getGameAndEnv = async () => {
    setLoading(true);
    const gameRes = await fetchGameList();
    setGameList(gameRes || []);

    const envRes = await fetchEnvCollectionList(studioId);

    if (envRes && gameRes) {
      const envDataList = gameRes.map((item) => {
        const envs: any = [];
        envRes?.forEach((env) => {
          if (item.gameID === env.gameID) {
            envs.push(env);
          }
        });
        return { name: item.gameID, envs };
      });
      const envCollectionList = envDataList.reduce(
        (acc: any, { name, envs }) => {
          acc[name] = envs;
          return acc;
        },
        {}
      );
      setAllEnvList(envCollectionList);
    }
    setLoading(false);
  };

  const handleDownload = (record?: IBillList) => {
    if (isDisabledDown) {
      return;
    }
    let tableData = [];
    const title = [
      getFormatMessage('bill_management_service'),
      getFormatMessage('bill_management_sub_service'),
      getFormatMessage('order_game'),
      getFormatMessage('order_env'),
      getFormatMessage('bill_management_region'),
      getFormatMessage('bill_management_type'),
      getFormatMessage('bill_management_mode'),
      getFormatMessage('bill_management_time_start'),
      getFormatMessage('bill_management_time_end'),
      getFormatMessage('bill_management_amount_payable'),
    ];
    if (record) {
      tableData = [
        title,
        [
          record.order?.service_name || '',
          record.order?.subservice_name || '',
          renderGameAndEnv(record.order)[0] || '',
          renderGameAndEnv(record.order)[1] || '',
          renderArea(record.order) || '',
          renderOrderType(record.order) || '',
          renderOrderMode(record.order) || '',
          getLocalTime(
            record.bill_start_date,
            userInfo?.UserSettings?.Timezone
          ) || '',
          getLocalTime(
            record.bill_end_date,
            userInfo?.UserSettings?.Timezone
          ) || '',
          getPrice(record.total_cost || '').amount || '',
        ],
      ];
    } else {
      tableData = [
        title,
        ...billList.map((item) => [
          item.order?.service_name || '',
          item.order?.subservice_name || '',
          renderGameAndEnv(item.order)[0] || '',
          renderGameAndEnv(item.order)[1] || '',
          renderArea(item.order) || '',
          renderOrderType(item.order) || '',
          renderOrderMode(item.order) || '',
          getLocalTime(
            item.bill_start_date,
            userInfo?.UserSettings?.Timezone
          ) || '',
          getLocalTime(item.bill_end_date, userInfo?.UserSettings?.Timezone) ||
            '',
          getPrice(item.total_cost || '').amount || '',
        ]),
      ];
    }
    const tableDataAsString = tableData.map((row) =>
      row.map((cell) => String(cell))
    );
    downCSVTemplate('billing_data', tableDataAsString);
  };

  const renderArea = (value: IBillOrder | null) => {
    if (!value) {
      return '';
    }
    if (value.area === BillOrderArea.Singapore) {
      return 'Singapore';
    }
    if (value.area === BillOrderArea.China) {
      return 'China';
    }
    if (value.area === BillOrderArea.Woa) {
      return 'Woa';
    }
    return '';
  };

  const renderOrderType = (value: IBillOrder | null) => {
    if (!value) {
      return '';
    }
    if (value.order_type === BillOrderType.NewlyPurchased) {
      return getFormatMessage('order_new_pay');
    }
    if (value.order_type === BillOrderType.Renew) {
      return getFormatMessage('order_renew');
    }
    return '';
  };

  const renderOrderMode = (value: IBillOrder | null) => {
    if (!value) {
      return '';
    }
    if (value.order_mode === BillOrderMode.Postpaid) {
      return getFormatMessage('transaction_type_post_paid');
    }
    if (value.order_mode === BillOrderMode.Prepayment) {
      return getFormatMessage('transaction_type_pre_paid');
    }
    return '';
  };

  const renderGameAndEnv = (value: IBillOrder | null) => {
    if (!value) {
      return [];
    }
    const gameItem = gameList.find(
      (item) => item.gameID === value.game_id.toString()
    );
    const envs = gameItem?.gameID ? allEnvList[value.game_id] : [];
    const envOptions = formatEnvCollectionOptions(envs);
    let envName = '';
    envOptions.length !== 0 &&
      envOptions.forEach((item) => {
        const envItem = item.children?.find(
          (it) => it.envIncrementID === value.env_id
        );
        if (envItem) {
          envName = `${item.label}-${envItem.envName}`;
        }
      });
    return [gameItem?.gameName || '', envName];
  };

  const COLUMNS = [
    {
      name: getFormatMessage('bill_management_service'),
      key: 'order',
      tableExtension: {
        renderRow(_key: any, value: IBillOrder | null) {
          return <>{value?.service_name}</>;
        },
      },
    },
    {
      name: getFormatMessage('bill_management_sub_service'),
      key: 'order',
      tableExtension: {
        renderRow(_key: any, value: IBillOrder | null) {
          return <>{value?.subservice_name}</>;
        },
      },
    },
    {
      name: getFormatMessage('order_game'),
      key: 'order',
      tableExtension: {
        renderRow(_key: any, value: IBillOrder | null) {
          if (value) {
            return <>{renderGameAndEnv(value)[0]}</>;
          }
          return null;
        },
      },
    },
    {
      name: getFormatMessage('order_env'),
      key: 'order',
      tableExtension: {
        renderRow(_key: any, value: IBillOrder | null) {
          if (value) {
            return <>{renderGameAndEnv(value)[1]}</>;
          }
          return null;
        },
      },
    },
    {
      name: getFormatMessage('bill_management_region'),
      key: 'order',
      tableExtension: {
        renderRow(_key: any, value: IBillOrder | null) {
          if (value) {
            return <>{renderArea(value)}</>;
          }
          return null;
        },
      },
    },
    {
      name: getFormatMessage('bill_management_type'),
      key: 'order',
      tableExtension: {
        renderRow(_key: any, value: IBillOrder | null) {
          if (!value) {
            return null;
          }
          return <>{renderOrderType(value)}</>;
        },
      },
    },
    {
      name: getFormatMessage('bill_management_mode'),
      key: 'order',
      tableExtension: {
        renderRow(_key: any, value: IBillOrder | null) {
          if (!value) {
            return null;
          }
          return <>{renderOrderMode(value)}</>;
        },
      },
    },
    {
      name: getFormatMessage('bill_management_time_start'),
      key: 'bill_start_date',
      tableExtension: {
        renderRow(_key: any, value: string | Date | undefined) {
          return <>{getLocalTime(value, userInfo?.UserSettings?.Timezone)}</>;
        },
      },
    },
    {
      name: getFormatMessage('bill_management_time_end'),
      key: 'bill_end_date',
      tableExtension: {
        renderRow(_key: any, value: string | Date | undefined) {
          return <>{getLocalTime(value, userInfo?.UserSettings?.Timezone)}</>;
        },
      },
    },
    {
      name: getFormatMessage('bill_management_amount_payable'),
      key: 'total_cost',
      tableExtension: {
        renderRow(_key: any, value: string) {
          return <span>{getPrice(value).amount}</span>;
        },
      },
    },
    {
      name: getFormatMessage('operate'),
      key: 'action',
      tableExtension: {
        renderRow(_key: any, _value: any, row: IListItem) {
          const record = row as IBillList;
          return (
            <span className="link-url" onClick={() => handleDownload(record)}>
              {getFormatMessage('download_details')}
            </span>
          );
        },
      },
    },
  ];
  const onChangedFilterValues = () => {};
  const pageList = [...billList].slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="bi-recharge-bottom">
      <div className="recharge-title recharge-title-last">
        <button
          className="gc-btn gc-btn-default balance-container"
          disabled={isDisabledDown}
          onClick={() => handleDownload()}
        >
          <img src={downloadBalanceIcon} width={16} height={16} />
          {getFormatMessage('download_all')}
        </button>
      </div>
      <AutoTable
        loading={loading}
        list={pageList || []}
        configFields={COLUMNS}
        tableExtension={{
          onChange: (changeInfo) => {
            if (!changeInfo) {
              return;
            }
            setCurrentPage(changeInfo.current);
            setPageSize(changeInfo.pageSize || 10);
          },
          pagination: {
            current: currentPage,
            total: billList.length,
            pageSize,
            showTotal: () => {
              return getFormatMessage('total_items', {
                params: {
                  total: billList.length,
                },
              });
            },
          },
        }}
        layoutExtension={{
          showTopbar: false,
          showFilter: true,
        }}
        onChangedFilterValues={onChangedFilterValues}
      />
    </div>
  );
};
